<template>
  <div class="row" v-if="shouldLoad">
    <div class="col-md-12">
      <div class="block" style="position: relative;">
        <div
          style="position: absolute; z-index: 10; right: 15px; top: 15px; text-align: right;"
        >
          <b-button @click="isHidden = !isHidden" class="btn-de" size="sm"
            ><span v-if="isHidden">Change Month</span
            ><span v-else>Close</span></b-button
          >

          <div style="margin-top: 5px;">
            <month-picker
              :no-default="true"
              :min-date="validFrom"
              :max-date="today"
              v-if="!isHidden"
              @input="dateInput"
            ></month-picker>
          </div>

          <div class="graph-toggle" @click="changeGraphToggle" v-if="isHidden">
            <span v-bind:class="{ selected: graphIsConsumption }" class="item"
              >kWh</span
            >
            <span v-bind:class="{ selected: !graphIsConsumption }" class="item"
              >£</span
            >
          </div>
        </div>
        <div @click="hideDate">
          <v-chart
            class="chart"
            :option="bar"
            ref="bar"
            autoresize
            :loading="barLoading"
            :loadingOptions="barLoadingOptions"
          />
        </div>

        <div
          class="graph-totals"
          v-if="currentData.totalCost && currentData.totalUsage"
        >
          <div class="header">
            Estimated Usage <span v-if="!graphIsConsumption">Cost</span>
          </div>

          <div v-if="!graphIsConsumption" class="total">
            &pound;{{ currentData.totalCost }}
          </div>
          <div v-else class="total">
            {{ currentData.totalUsage }}
            <span>{{ getElement("unit", unit) }}</span>
          </div>

          <div v-if="totalSubLine" class="subData no-gap">
            {{ totalSubLine }}
          </div>

          <div v-if="!graphIsConsumption" class="subData">
            <strong>Please note:</strong> Calculations are estimated and exclude
            fixed charges. Please refer to your bills for accurate cost and
            usage
          </div>
        </div>

        <div class="download-header">Download Meter Raw Data</div>

        <b-button @click="downloadReads" class="btn-de" size="sm"
          >Download Data</b-button
        >

        <div class="data-available">Data available from {{ dataFromDate }}</div>

        <vue-json-to-csv
          ref="readDownload"
          :csv-title="getDownloadName(false)"
          :labels="downloadLabels"
          :json-data="readDownloadData"
          ><span></span>
        </vue-json-to-csv>
      </div>
    </div>
  </div>
</template>

<script>
import "@/plugins/echarts";

import VChart from "vue-echarts";

import { MonthPicker } from "vue-month-picker";
import VueJsonToCsv from "vue-json-to-csv";
import { utilities } from "@/components/mixins/utilities";
import { stringFunctions } from "@/components/mixins/string-functions";
import { date } from "@/components/mixins/date";

export default {
  name: "ReadData",
  mixins: [utilities, stringFunctions, date],
  props: {
    id: {
      type: String,
      required: true
    },
    utility: {
      type: String,
      required: true
    },
    archivedMeterEndDate: {
      type: String,
      required: true
    },
    initialData: {
      type: Object,
      required: true
    }
  },
  watch: {
    utility() {
      this.changeUtility();
    }
  },
  components: {
    VChart,
    MonthPicker,
    VueJsonToCsv
  },
  computed: {
    today() {
      return new Date();
    },
    dataFromDate() {
      return this.formatDate(this.globalFrom, true);
    },
    validFrom() {
      const date = new Date(this.globalFrom);

      date.setDate(1);

      return date;
    },
    globalFrom() {
      if (this.initialData.validFrom) {
        return this.initialData.validFrom;
      }

      return null;
    },
    totalSubLine() {
      if (
        !this.currentData.includesPartial &&
        !this.currentData.includesEstimates
      ) {
        return null;
      }

      let response = "";
      if (this.currentData.includesPartial) {
        response += "partial";
      }

      if (this.currentData.includesEstimates) {
        response += response.length > 0 ? " and estimated" : "estimated";
      }

      return "(This calculation includes " + response + " data)";
    }
  },
  data() {
    return {
      shouldLoad: false,
      graphIsConsumption: true,
      currentData: {},
      downloadLabels: {
        date: { title: "Read Date" },
        read: { title: "Read Value" },
        isEstimate: { title: "Is Estimated" },
        consumption: { title: "Consumption" },
        cost: { title: "Consumption Cost" }
      },
      isHidden: true,
      unit: null,
      date: {
        from: null,
        to: null,
        month: null,
        year: null
      },
      month: "",
      bar: {
        grid: {
          left: "3%",
          right: "3%",
          top: "10%",
          bottom: "10%",
          containLabel: true
        },
        title: {
          left: "center",
          text: "April 2023",
          subtext: "Consumption",
          itemGap: 2
        },
        tooltip: {
          trigger: "axis",
          formatter: params => {
            if (
              (typeof params[0].value === "undefined" ||
                params[0].value === null) &&
              !params[0].data.isMissing
            ) {
              return;
            }
            if (params[0].data.isMissing) {
              return `<strong>${params[0].name} ${this.month}</strong><br />
                <strong>Missing data</strong><br><span style="font-size: 0.7rem; font-weight: bold; color: #888;">This can be expected if out of signal</span>`;
            }

            let prefix = "";
            let suffix = " " + this.getElement("unit", this.unit);
            if (!this.graphIsConsumption) {
              prefix = "£";
              suffix = "";
            }

            let response = `${params[0].data.day} ${params[0].name} ${this.month}<br />
              <strong>${prefix}${params[0].value}${suffix}</strong>`;

            if (params[0].data.isEstimated) {
              response = `${response} <br /><span style="font-size: 0.7rem; font-weight: bold; color: #888;">This is an estimate</span>`;
            }

            return response;
          }
        },
        xAxis: {
          type: "category",
          data: []
        },
        yAxis: {
          name: "Heat [kWh]",
          type: "value"
        },
        series: [
          {
            name: "Data",
            stack: "Ad",
            color: ["#83C45D"],
            data: [],
            type: "bar"
          }
        ]
      },
      barLoading: false,
      barLoadingOptions: {
        text: "Loading…",
        color: "#4ea397",
        maskColor: "rgba(255, 255, 255, 0.4)"
      },
      readDownloadData: []
    };
  },
  created() {
    this.initiate();
  },
  methods: {
    changeGraphToggle() {
      this.graphIsConsumption = !this.graphIsConsumption;

      this.$nextTick(() => {
        this.loadData(this.currentData);
      });
    },
    refreshComplete() {
      this.$emit("refresh-complete");
    },
    getDownloadName() {
      return this.stringToSlug(this.bar.title.text);
    },
    downloadReads() {
      if (this.readDownloadData.length === 0) {
        this.$store.dispatch("pushError", {
          title: "No data available",
          subtitle: "No read data available to download",
          samePage: true
        });
        return;
      }
      this.$gtag.event("downloadReads", {
        event_category: "billingDashboard"
      });

      this.$nextTick(() => {
        this.$refs.readDownload.$el.click();
      });
    },
    changeUtility() {
      let date = new Date();

      if (this.archivedMeterEndDate !== "") {
        date = new Date(this.archivedMeterEndDate);
        this.refresh(date.getMonth() + 1, date.getFullYear());
      }

      this.refresh(date.getMonth() + 1, date.getFullYear());
    },
    hideDate() {
      this.isHidden = true;
    },
    dateInput(date) {
      if (
        this.date.monthIndex === date.monthIndex &&
        this.date.year === date.year
      ) {
        this.isHidden = true;
        return;
      }
      this.date = date;
      this.isHidden = true;

      this.refresh(this.date.monthIndex, this.date.year);
    },
    initiate() {
      this.loadData(this.initialData);
    },
    loadData(data) {
      if (data.subAccountActive) {
        return;
      }
      this.currentData = data;
      this.shouldLoad = true;
      this.barLoading = true;
      this.month = data.name;
      this.unit = data.unit;
      this.bar.xAxis.data = data.axisKeys;
      this.bar.yAxis.name = this.graphIsConsumption
        ? this.getElement("unit", this.unit)
        : "£";
      this.bar.title.text = `${data.fullName} ${this.getElement(
        "name",
        this.unit
      )}`;
      this.bar.title.subtext = this.graphIsConsumption
        ? "Consumption"
        : "Consumption Cost (excluding fixed charges)";
      this.readDownloadData = [];

      let graphData = [];
      let maxValue = 2;

      const missingData = {
        color: "#a90000"
      };

      const estimatedData = {
        color: "#ccc"
      };

      Object.entries(data.reads).forEach(([dateKey, value]) => {
        let style = {};

        if (value.is_missing) {
          style = missingData;
        }

        if (!value.is_missing && value.is_estimate) {
          style = estimatedData;
        }

        let costValue = value.cost;
        if (costValue) {
          costValue = value.cost.toFixed(2);
        }

        const graphValue = this.graphIsConsumption
          ? value.consumption
          : costValue;

        graphData.push({
          value: value.is_missing ? 0.05 : graphValue,
          day: value.day_of_week,
          isMissing: value.is_missing,
          isEstimated: value.is_estimate,
          itemStyle: style
        });

        if (!value.is_missing && value.consumption !== null) {
          this.readDownloadData.push({
            date: data.monthPrefix + "-" + dateKey,
            read: value.read_value,
            isEstimate: value.is_estimate ? "Y" : "",
            consumption: value.consumption,
            cost: value.cost
          });
        }

        if (graphValue && maxValue < graphValue) {
          maxValue = graphValue;
        }
      });

      this.bar.yAxis.max = Math.ceil((maxValue + 1) / 5) * 5;
      this.bar.series[0].data = graphData;
      this.barLoading = false;
    },

    refresh(month, year) {
      this.barLoading = true;

      const data = {
        month: month,
        year: year,
        utilityReference: this.utility
      };

      this.$store
        .dispatch("dashboardBillingReads", { id: this.id, data: data })
        .then(response => {
          this.$gtag.event("requestReads", {
            event_category: "billingDashboard"
          });

          this.loadData(response);
        })
        .catch(err => {
          const title = "Problem getting read data";
          const subtitle = err;
          const samePage = true;
          this.$store.dispatch("pushError", { title, subtitle, samePage });
        })
        .finally(() => {
          this.barLoading = false;
          this.refreshComplete();
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.chart {
  height: 500px;
  padding-top: 40px;
}

.download-header {
  font-weight: bold;
  font-size: 1rem;
}

.data-available {
  color: #777;
  font-size: 0.8rem;
  padding-top: 20px;
}

.graph-toggle {
  background-color: #83c45d;
  border-radius: 10px;
  display: inline-block;

  .item {
    padding: 0px 10px;
    border-radius: 10px;
    cursor: pointer;

    &.selected {
      border: 1px solid darkgreen;
      background-color: darkgreen;
      color: white;
    }
  }
}

.graph-totals {
  margin-bottom: 2rem;
  .header {
    color: #777;
    font-size: 0.8rem;
  }

  .total {
    font-size: 2rem;
    font-weight: bold;

    span {
      font-size: 1.2rem;
    }
  }

  .subData {
    color: #777;
    font-size: 0.8rem;
    margin-top: 15px;

    &.no-gap {
      margin-top: 0;
    }
  }
}
</style>
